import React from 'react';
import {
  YesNoQuestion,
  YesNoCommentsQuestion,
  YesNoPartiallyQuestion,
  YesNoTimeQuestion,
  YesNoExplanationIfNoQuestion,
  YesNoExplanationIfYesQuestion,
  YesNoLateOpeningReasonsQuestion,
  QuestionAnswer,
  QuestionInterface
} from './questions/QuestionTypes';

// We need to create new question types for Number inputs and Party Representatives
interface NumberQuestionProps {
  id: string;
  question: string;
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  required?: boolean;
  error?: string;
}

const NumberQuestion: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      className="w-32 input"
      min="0"
      value={value.answer || ''}
      onChange={(e) => onChange({ ...value, answer: e.target.value })}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

const YesNoTimeDelayQuestion: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex mb-2 space-x-4">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'Yes' && (
      <div>
        <label className="text-sm label">How long did it take to resolve? (minutes)</label>
        <input
          type="number"
          className="w-32 input"
          min="0"
          placeholder="Minutes"
          value={value.details || ''}
          onChange={(e) => onChange({ ...value, details: e.target.value })}
        />
      </div>
    )}
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

interface Question1Data {
  [questionId: string]: QuestionAnswer;
}

interface Step3Question1Props {
  data: Question1Data;
  onChange: (data: Question1Data) => void;
  errors: Record<string, string>;
  selectedTimeline: string;
  questionNumber?: number;
  questionTitle?: string;
  questionDescription?: string;
  currentSection?: number;
  totalSections?: number;
}

// Timeline-based questions for Question 1 (Station Opening/Closing)
const TIMELINE_QUESTIONS: Record<string, QuestionInterface[]> = {
  morning: [
    // 1a
    { id: '1a', question: 'Did the Polling Centre open on time?', type: 'yes_no_late_opening_reasons', required: true },
    // 1b
    { id: '1b', question: 'Was there a long voting queue/line when the Polling Centre opened?', type: 'yes_no', required: true }
  ],
  afternoon: [
    // 10a
    { id: '10a', question: 'Was the polling Centre still open?', type: 'yes_no', required: true },
    // 10b
    { id: '10b', question: 'Was there a long voting queue/line in the afternoon hours?', type: 'yes_no', required: true }
  ],
  vote_count: [
    // 20a
    { id: '20a', question: 'What time did the Polling Centre close?', type: 'time', required: true },
    // 21a
    { id: '21a', question: 'Were ballot papers counted at the Polling Centre?', type: 'yes_no', required: true },
    // 21b
    { id: '21b', question: 'Was there adequate light for ballot counting at the Polling Centre?', type: 'yes_no', required: true },
    // 22a
    { id: '22a', question: 'The ballot papers were transported somewhere else for counting?', type: 'yes_no', required: true }
  ]
};

// Yes/No with explanation if No component for question 21a
const YesNoExplanationIfNoCustomQuestion: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex mb-2 space-x-4">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'No' && (
      <div>
        <label className="text-sm label">Please explain why this was the case:</label>
        <textarea
          className="w-full h-20 resize-none input"
          placeholder="Explain why the polling station was not open..."
          value={value.details || ''}
          onChange={(e) => onChange({ ...value, details: e.target.value })}
        />
      </div>
    )}
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

// Time input component for question 30a
const TimeInputQuestion: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="time"
      className="w-40 input"
      value={value.answer || ''}
      onChange={(e) => onChange({ ...value, answer: e.target.value })}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

const QUESTION_COMPONENTS = {
  'yes_no': YesNoQuestion,
  'yes_no_comments': YesNoCommentsQuestion,
  'yes_no_partially': YesNoPartiallyQuestion,
  'yes_no_time': YesNoTimeQuestion,
  'yes_no_explanation_if_no': YesNoExplanationIfNoQuestion,
  'yes_no_explanation_if_yes': YesNoExplanationIfYesQuestion,
  'yes_no_late_opening_reasons': YesNoLateOpeningReasonsQuestion, // Multiple checkbox reasons
  'number': NumberQuestion,
  'yes_no_time_delay': YesNoTimeDelayQuestion,
  'yes_no_explanation_if_no_custom': YesNoExplanationIfNoCustomQuestion,
  'time': TimeInputQuestion,
};

const Step3Question1: React.FC<Step3Question1Props> = ({ data, onChange, errors, selectedTimeline }) => {
  const updateQuestion = (questionId: string, value: QuestionAnswer) => {
    onChange({
      ...data,
      [questionId]: value
    });
  };

  // Get questions for the selected timeline with conditional filtering
  const getQuestionsForTimeline = () => {
    if (!selectedTimeline || !TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS]) {
      return [];
    }

    const allQuestions = TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS];

    // Filter out conditional questions that shouldn't be shown
    return allQuestions.filter(question => {
      if (!question.conditional) return true;

      const dependentAnswer = data?.[question.conditional.dependsOn]?.answer;
      return dependentAnswer === question.conditional.showWhen;
    });
  };

  const questions = getQuestionsForTimeline();

  // Organize questions by categories for better display
  const getQuestionsByCategory = () => {
    const categories = [
      {
        title: '1. Station Opening',
        questions: questions.filter(q => q.id.startsWith('1'))
      },
      {
        title: '2. Personnel Present',
        questions: questions.filter(q => q.id.startsWith('2'))
      },
      {
        title: '3. Security & Location',
        questions: questions.filter(q => q.id.startsWith('3'))
      },
      {
        title: '4. Equipment & Materials',
        questions: questions.filter(q => q.id.startsWith('4'))
      },
      {
        title: '5. Voter Verification',
        questions: questions.filter(q => q.id.startsWith('5'))
      },
      {
        title: '6. Voting Procedures',
        questions: questions.filter(q => q.id.startsWith('6'))
      },
      {
        title: '7. Facility Conditions',
        questions: questions.filter(q => q.id.startsWith('7'))
      },
      {
        title: '8. Accessibility',
        questions: questions.filter(q => q.id.startsWith('8'))
      },
      {
        title: '9. Observers & Complaints',
        questions: questions.filter(q => q.id.startsWith('9'))
      }
    ];

    return categories.filter(cat => cat.questions.length > 0);
  };

  const questionCategories = getQuestionsByCategory();

  const getCompletionStatus = () => {
    const completed = questions.filter(q => data?.[q.id]?.answer).length;
    return { completed, total: questions.length };
  };

  const { completed, total } = getCompletionStatus();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Question 1: Station Opening
        </h2>
        <p className="mb-4 text-gray-600">
          Timeline: {selectedTimeline ? selectedTimeline.charAt(0).toUpperCase() + selectedTimeline.slice(1).replace('_', ' ') : 'No timeline selected'}
        </p>
        {!selectedTimeline && (
          <div className="p-4 mb-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-yellow-800">Please select a timeline in Step 1 to see relevant questions for Question 1.</p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="p-3 mb-6 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">Section A Progress</span>
            <span className="text-sm text-blue-800">{completed}/{total} completed</span>
          </div>
          <div className="h-2 mt-2 bg-blue-200 rounded-full">
            <div
              className="h-2 transition-all duration-300 bg-blue-600 rounded-full"
              style={{ width: `${(completed / total) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Questions organized by categories */}
      <div className="space-y-8">
        {questionCategories.length > 0 ? questionCategories.map((category) => (
          <div key={category.title} className="space-y-4">
            {/* Category Header */}
            <h3 className="flex items-center pb-2 text-lg font-medium text-gray-900 border-b border-gray-200">
              <span className="px-2 py-1 mr-3 text-sm font-semibold text-blue-800 bg-blue-100 rounded-md">
                {category.title.split('.')[0]}
              </span>
              {category.title}
            </h3>

            {/* Category Questions */}
            <div className="ml-4 space-y-4">
              {category.questions.map((question) => {
                const QuestionComponent = QUESTION_COMPONENTS[question.type as keyof typeof QUESTION_COMPONENTS];
                const questionValue = data?.[question.id] || {};
                const questionError = errors?.[question.id];

                return (
                  <div key={question.id} className="p-4 transition-colors bg-white border border-gray-200 rounded-lg hover:border-blue-300">
                    <QuestionComponent
                      id={question.id}
                      question={`${question.id}: ${question.question}`}
                      value={questionValue}
                      onChange={(value) => updateQuestion(question.id, value)}
                      required={question.required}
                      error={questionError}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )) : (
          <div className="py-8 text-center text-gray-500">
            {selectedTimeline ? 'No questions available for this timeline.' : 'Select a timeline to view questions.'}
          </div>
        )}
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 className="mb-2 font-medium text-red-900">Please complete the following required questions:</h4>
          <ul className="space-y-1 text-sm text-red-800">
            {Object.entries(errors).map(([questionId, error]) => (
              <li key={questionId}>• {questionId}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Section Summary */}
      <div className="p-4 rounded-lg bg-gray-50">
        <h4 className="mb-2 font-medium text-gray-900">Summary</h4>
        <p className="text-sm text-gray-700">
          This section covers the initial setup and preparation of the polling station before voting begins.
          All questions in this section are required to proceed to the next section.
        </p>
        <div className="mt-2 text-sm text-gray-600">
          Completion: {completed}/{total} questions ({Math.round((completed / total) * 100)}%)
        </div>
      </div>
    </div>
  );
};

export default Step3Question1;